import type { Router } from 'vue-router'
import { useCookieConsentStore } from '@/store/cookieConsent'

// Domain registered in the Cookiebot console; shared with med-web-funnel
// (one Cookiebot tenant covers the whole product surface).
const COOKIEBOT_ID = '325efe53-0b3b-4b2e-b352-548bb04613aa'

/**
 * Install a router hook that injects the Cookiebot loader the first time the
 * user lands on any route OTHER than `/signin`. See [ADR 0007](../docs/decisions/0007-cookiebot.md).
 *
 * Two reasons we don't ship it as a static `<script>` in `index.html`:
 *
 * 1. **`/signin` is a transient bounce page** — the user is on it for
 *    ~milliseconds before being redirected to Auth0. Loading the consent banner
 *    there is wasted bandwidth (the banner UI never gets a chance to render)
 *    and visually disruptive if it does flash. The eventual real `/status` /
 *    `/cancel/*` landing is where the banner belongs.
 * 2. **PR-preview hosts (`*.workers.dev`)** aren't in the Cookiebot domain
 *    group; the SDK 404s with "domain not authorized" on those hosts. We
 *    explicitly opt out so preview consoles stay clean.
 *
 * The loader fires exactly once — the first non-`/signin` navigation triggers
 * it, subsequent navigations are no-ops. If the user happens to land on
 * `/signin` first (the common Auth0 round-trip) the loader fires immediately
 * after the post-callback redirect into `/status` / `/cancel/*`.
 */
export function installCookiebotLoader(router: Router): void {
	if (location.hostname.toLowerCase().endsWith('.workers.dev')) return

	let loaded = false
	router.afterEach((to) => {
		if (loaded) return
		if (to.path === '/signin') return
		loaded = true
		const s = document.createElement('script')
		s.id = 'Cookiebot'
		s.src = 'https://consent.cookiebot.com/uc.js'
		s.setAttribute('data-cbid', COOKIEBOT_ID)
		// No `data-blockingmode`: the mode comes from the Cookiebot console, same as the
		// funnel's GTM-loaded tag. Requesting "auto" here made uc.js patch
		// EventTarget.prototype.addEventListener, which swallowed the `load` listener
		// Stripe.js's loader attaches to its own script — the payment form then waited on a
		// promise that never settled (DEV-35323). See ADR 0007.
		s.type = 'text/javascript'
		document.head.appendChild(s)
	})
}

/**
 * Sync Cookiebot's consent state into the `cookieConsent` Pinia store. Call once at bootstrap
 * (main.ts, after Pinia is installed) so the Sentry consent gate — which subscribes to that
 * store — sees `statistics` consent the moment Cookiebot reports it.
 *
 * Cookiebot fires window events as the banner is answered; we also sync immediately for the
 * returning-user / opt-out case where `window.Cookiebot` already carries a response before the
 * listeners attach. Mirrors med-web-funnel's `useCookiebot()` (the consent-sync half only — no
 * analytics/Jet wiring here). The listeners are process-global and never removed: there is one
 * app instance per page load, so there is nothing to clean up.
 */
export function setupCookieConsentSync(): void {
	const consent = useCookieConsentStore()

	const sync = () => {
		const cookiebot = window.Cookiebot
		if (!cookiebot?.consent) return
		consent.setInited(true)
		consent.setResponded(Boolean(cookiebot.hasResponse))
		consent.setCategories({
			necessary: cookiebot.consent.necessary,
			preferences: cookiebot.consent.preferences,
			statistics: cookiebot.consent.statistics,
			marketing: cookiebot.consent.marketing,
		})
	}

	window.addEventListener('CookiebotOnConsentReady', sync)
	window.addEventListener('CookiebotOnAccept', sync)
	window.addEventListener('CookiebotOnDecline', sync)

	if (window.Cookiebot?.hasResponse) sync()
}
